import React, { useEffect } from 'react';

import {
  Breakpoints,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  ThemeProvider,
  useIsMobile,
} from '@vw-marketing/us-components';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import { AppConstants } from '../../../../shared/utils/app-constants';
import { onVWBasicStepLoadEvent, onVWBasicLayerLoadEvent } from '../../../../shared/tagging/tagging-helpers';
import AppButtonView from '../app-button/AppButtonView';
import QrCode from '../qr-code';
import {
  StyledAddVehicleAppButtonsContainer,
  StyledAddVehicleContainer,
  StyledAddVehicleDescription,
  StyledAddVehicleQrCodeContainer,
  StyledAddVehicleSubtitleAndDescription,
} from './styled';

export interface AddVehicleViewProps {
  translationHandler: (key: string) => string;
  trackingManager: any;
  isCanada: boolean;
  baseUrl: string;
}

const AddVehicleView = ({ translationHandler, trackingManager, isCanada, baseUrl }: AddVehicleViewProps) => {
  const isMobile = useIsMobile(Breakpoints.b1600);

  useEffect(() => {
    try {
      if (isCanada) {
        onVWBasicLayerLoadEvent(trackingManager, 'Vehicle Details - Add Vehicle');
      } else {
        onVWBasicStepLoadEvent(trackingManager, {
          Name: 'Vehicle Management',
          StepName: 'Vehicle Details - Add Vehicle',
          StepNumber: '5.3',
        });
      }
    } catch (error) {
      console.warn('Error tracking add vehicle interaction layer load:', error);
    }
  }, [trackingManager]);

  const appStoreUrl = isCanada
    ? 'https://apps.apple.com/ca/app/myvw/id1580947985'
    : 'https://apps.apple.com/us/app/myvw/id1481486650';

  const googlePlayUrl = isCanada
    ? 'https://play.google.com/store/apps/details?id=com.vw.carnet.releaseca&hl=en_CA'
    : 'https://play.google.com/store/apps/details?id=com.vw.carnet.release';

  return (
    <ThemeProvider theme="main">
      <StyledAddVehicleContainer>
        <StyledAddVehicleSubtitleAndDescription>
          <TextCustom
            style={{
              fontSize: TextSize.TextSize20,
              fontWeight: TextWeight.regular,
              lineHeight: TextLineHeight.TextLineHeight32,
              color: TextColor.primary,
            }}
          >
            {applyTextTransformations(
              translationHandler('vehicleManagement.interaction.layer.add.vehicle.subtitle'),
              ['myVW'],
            )}
          </TextCustom>
          <StyledAddVehicleDescription>
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
              }}
            >
              {applyTextTransformations(
                translationHandler('vehicleManagement.interaction.layer.add.vehicle.description'),
                ['myVW'],
              )}
            </TextCustom>
          </StyledAddVehicleDescription>
        </StyledAddVehicleSubtitleAndDescription>
        {isMobile ? (
          <StyledAddVehicleAppButtonsContainer>
            <AppButtonView image={AppConstants.AppStoreButton} link={appStoreUrl} type="APP_STORE" trackingManager={trackingManager} />
            <AppButtonView image={AppConstants.GooglePlayButton} link={googlePlayUrl} type="GOOGLE_PLAY" trackingManager={trackingManager} />
          </StyledAddVehicleAppButtonsContainer>
        ) : (
          <StyledAddVehicleQrCodeContainer>
            <QrCode url={`${baseUrl}/downloadApp${isCanada ? '?countryCode=CA' : ''}`} />
            <TextCustom
              appearance={TextAppearance.copy100}
              style={{
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
                color: TextColor.secondary,
              }}
            >
              {translationHandler('vehicleManagement.interaction.layer.add.vehicle.qr.code.description')}
            </TextCustom>
          </StyledAddVehicleQrCodeContainer>
        )}
      </StyledAddVehicleContainer>
    </ThemeProvider>
  );
};

export default AddVehicleView;
